/**
 * QoCAwards.js
 * Presentation-ready Clause Champions / Awards page.
 * Shows top performing facility per clause for a given period,
 * plus an overall "Facility of the Period" grand prize.
 *
 * Route suggestion: /qoc/awards
 *
 * Design note: this page is meant to be opened in full-screen / projected
 * during an actual award ceremony, so it favours large, celebratory visuals
 * (medals, big facility names) over data density.
 */
import React, { useState, useEffect } from 'react';
import { Select, Spin, Empty, Button, Tag, InputNumber, Tooltip, Radio } from 'antd';
import {
    TrophyOutlined, CrownOutlined, StarFilled, ReloadOutlined,
    FullscreenOutlined, FilePdfOutlined, LoadingOutlined,
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import moment from 'moment';

const MEDAL = ['🥇', '🥈', '🥉'];
const MEDAL_COLOR = ['#d4af37', '#a8a8a8', '#b08d57'];

const RATING_LABEL = (score) => {
    if (score >= 98) return { label: 'Outstanding', color: '#389e0d' };
    if (score >= 95) return { label: 'Excellent', color: '#1890ff' };
    if (score >= 90) return { label: 'Good', color: '#52c41a' };
    if (score >= 85) return { label: 'Fair', color: '#faad14' };
    if (score >= 70) return { label: 'Requires Improvement', color: '#fa8c16' };
    return { label: 'Inadequate', color: '#f5222d' };
};

function PodiumCard({ champion, podiumSize = 3 }) {
    const podium = (champion.podium || []).slice(0, podiumSize);
    const hasWinner = podium.length > 0;
    const rating = hasWinner ? RATING_LABEL(podium[0].score) : null;

    return (
        <div style={{
            background: '#fff', borderRadius: 16, padding: 20,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: hasWinner ? `2px solid ${MEDAL_COLOR[0]}55` : '1px solid #eee',
            minHeight: 280, display: 'flex', flexDirection: 'column',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Tag style={{ background: '#1890ff', color: '#fff', border: 'none', fontWeight: 800, fontSize: 13 }}>
                    {champion.clause_code}
                </Tag>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#333', flex: 1, lineHeight: 1.3 }}>
                    {champion.clause_title}
                </div>
            </div>

            {!hasWinner ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }}>—</div>
                        <div style={{ fontSize: 12 }}>No eligible facilities yet</div>
                    </div>
                </div>
            ) : (
                <div style={{ flex: 1 }}>
                    {podium.map((p, i) => (
                        <div key={p.facility_id} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 8px', marginBottom: 6,
                            background: i === 0 ? `${MEDAL_COLOR[0]}10` : '#fafafa',
                            borderRadius: 10,
                            border: i === 0 ? `1px solid ${MEDAL_COLOR[0]}40` : '1px solid #f0f0f0',
                        }}>
                            <span style={{ fontSize: i === 0 ? 26 : 20, width: 32, textAlign: 'center', flexShrink: 0 }}>
                                {MEDAL[i] || ''}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontWeight: i === 0 ? 800 : 600, fontSize: i === 0 ? 14 : 12.5,
                                    color: i === 0 ? '#1a1a2e' : '#555',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {p.facility_name}
                                </div>
                                <div style={{ fontSize: 10, color: '#999' }}>
                                    {p.region} · {p.assessment_count} assessment{p.assessment_count !== 1 ? 's' : ''}
                                </div>
                            </div>
                            <div style={{ fontWeight: 900, fontSize: i === 0 ? 18 : 14, color: MEDAL_COLOR[i] || '#888' }}>
                                {p.score.toFixed(1)}%
                            </div>
                        </div>
                    ))}
                    {champion.winners && champion.winners.length > 1 && (
                        <div style={{ fontSize: 10, color: '#d4af37', fontWeight: 700, marginTop: 4, textAlign: 'center' }}>
                            🏆 Joint winners — tied score
                        </div>
                    )}
                </div>
            )}

            {hasWinner && (
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                    <Tag style={{ background: rating.color, color: '#fff', border: 'none', fontSize: 10 }}>
                        {rating.label}
                    </Tag>
                </div>
            )}
        </div>
    );
}

function GrandPrizeBanner({ champion, periodLabel, rankMode }) {
    if (!champion) return null;
    const isQI = rankMode === 'qi';
    const headlineScore = isQI ? champion.quality_index : champion.avg_score;
    const rating = RATING_LABEL(headlineScore);
    return (
        <div style={{
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            borderRadius: 20, padding: '36px 40px', marginBottom: 32,
            color: '#fff', textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
            <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 160, opacity: 0.06 }}>
                <CrownOutlined />
            </div>
            <div style={{ fontSize: 12, letterSpacing: 3, opacity: 0.7, marginBottom: 10, textTransform: 'uppercase' }}>
                Facility of the Period · {periodLabel} · {isQI ? 'Quality Index' : 'Audit Score'} Ranking
            </div>
            <CrownOutlined style={{ fontSize: 40, color: '#facc15', marginBottom: 10 }} />
            <div style={{ fontSize: 30, fontWeight: 900, marginBottom: 6 }}>
                {champion.facility_name}
            </div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 14 }}>
                {champion.region} · {champion.site_type}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: '#facc15', lineHeight: 1 }}>
                        {headlineScore.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{isQI ? 'Quality Index' : 'Overall Average'}</div>
                </div>
                {isQI && (
                    <>
                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                                {champion.qoc_score.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.7 }}>Audit Score ({champion.qoc_weight}%)</div>
                        </div>
                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                                {champion.survey_composite.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.7 }}>Patient Survey ({champion.survey_weight}%)</div>
                        </div>
                    </>
                )}
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                <div>
                    <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1 }}>
                        {champion.assessment_count}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>Assessments</div>
                </div>
            </div>
            <Tag style={{ background: rating.color, color: '#fff', border: 'none', marginTop: 16, fontSize: 11 }}>
                {rating.label}
            </Tag>
            {isQI && (champion.inpatient_count + champion.outpatient_count) === 0 && (
                <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 10 }}>
                    ⚠ No patient surveys recorded yet — survey component currently scoring 0%
                </div>
            )}
        </div>
    );
}

function OverallLeaderboard({ list, rankMode, podiumSize = 3 }) {
    const isQI = rankMode === 'qi';
    const rows = (list || []).slice(0, podiumSize);
    if (rows.length <= 1) return null; // grand prize banner already covers #1 with only one entry

    return (
        <div style={{
            background: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 32,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#555', marginBottom: 12,
                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isQI ? 'Quality Index' : 'Audit Score'} — Top {rows.length}
            </div>
            {rows.map((f, i) => {
                const score = isQI ? f.quality_index : f.avg_score;
                const rating = RATING_LABEL(score);
                return (
                    <div key={f.facility_id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 8px', marginBottom: 6,
                        background: i === 0 ? '#fffbe6' : '#fafafa',
                        borderRadius: 10,
                        border: i === 0 ? '1px solid #facc1540' : '1px solid #f0f0f0',
                    }}>
                        <span style={{ fontSize: i < 3 ? 22 : 16, width: 30, textAlign: 'center', flexShrink: 0 }}>
                            {MEDAL[i] || `#${i + 1}`}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontWeight: i === 0 ? 800 : 600, fontSize: i === 0 ? 14 : 13,
                                color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {f.facility_name}
                            </div>
                            <div style={{ fontSize: 11, color: '#999' }}>
                                {f.region} · {f.site_type} · {f.assessment_count} assessment{f.assessment_count !== 1 ? 's' : ''}
                            </div>
                        </div>
                        {isQI && (
                            <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right', marginRight: 8 }}>
                                Audit {parseFloat(f.qoc_score || 0).toFixed(1)}%<br/>
                                Survey {parseFloat(f.survey_composite || 0).toFixed(1)}%
                            </div>
                        )}
                        <div style={{ fontWeight: 900, fontSize: i === 0 ? 18 : 15, color: rating.color, minWidth: 64, textAlign: 'right' }}>
                            {score.toFixed(1)}%
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function QoCAwards() {
    const [loading, setLoading] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [periodLabel, setPeriodLabel] = useState(null);
    const [minAssessments, setMinAssessments] = useState(1);
    const [champions, setChampions] = useState([]);
    const [overallChampions, setOverallChampions] = useState([]);
    const [clauseFilter, setClauseFilter] = useState([]); // empty = show all clauses
    const [podiumSize, setPodiumSize] = useState(3); // facilities shown per clause card
    const [rankMode, setRankMode] = useState('qi'); // 'qi' = Quality Index, 'audit' = raw audit score
    const [overallAudit, setOverallAudit] = useState([]); // audit-ranked overall list (fallback / comparison view)
    const [overallQI, setOverallQI] = useState([]); // QI-ranked overall list

    useEffect(() => {
        QoCService.getPeriods().then(r => {
            const active = (r.data?.data || []).filter(p => parseInt(p.is_active) === 1);
            setPeriods(active);
            const current = active.find(p => parseInt(p.is_current) === 1);
            if (current) setPeriodLabel(current.label);
        });
    }, []);

    useEffect(() => { if (periodLabel) { load(); setClauseFilter([]); } }, [periodLabel, minAssessments]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const filters = { period_label: periodLabel, min_assessments: minAssessments };
            const [champRes, auditRes, qiRes] = await Promise.all([
                QoCService.getClauseChampions ? QoCService.getClauseChampions(filters) : Promise.resolve({ data: { data: [] } }),
                QoCService.getOverallChampion ? QoCService.getOverallChampion(filters) : Promise.resolve({ data: { data: [] } }),
                QoCService.getOverallChampionByQI ? QoCService.getOverallChampionByQI(filters) : Promise.resolve({ data: { data: [] } }),
            ]);
            setChampions(champRes.data?.data || []);
            setOverallAudit(auditRes.data?.data || []);
            setOverallQI(qiRes.data?.data || []);
            // keep legacy state populated too, mirrors whichever the UI is currently using
            setOverallChampions(auditRes.data?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const qiUnavailable = rankMode === 'qi' && overallQI.length === 0;
    const effectiveRankMode = qiUnavailable ? 'audit' : rankMode;
    const grandPrize = effectiveRankMode === 'qi' ? (overallQI[0] || null) : (overallAudit[0] || null);
    const displayedChampions = clauseFilter.length
        ? champions.filter(c => clauseFilter.includes(c.clause_id))
        : champions;

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg,#0f172a,#1e293b)',
                padding: '20px 32px', display: 'flex', alignItems: 'center',
                gap: 14, flexWrap: 'wrap',
            }}>
                <TrophyOutlined style={{ fontSize: 28, color: '#facc15' }} />
                <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, color: '#fff', fontSize: 20, fontWeight: 800 }}>
                        Clause Champions & Recognition
                    </h2>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                        Eswatini Ministry of Health · National Quality Management Programme
                    </div>
                </div>
                <Select size="small" style={{ width: 180 }} placeholder="Select period"
                    value={periodLabel} onChange={setPeriodLabel}>
                    {periods.map(p => (
                        <Select.Option key={p.label} value={p.label}>
                            {p.is_current == 1 ? '★ ' : ''}{p.label}
                        </Select.Option>
                    ))}
                </Select>
                <Select mode="multiple" size="small" allowClear
                    placeholder="All clauses"
                    style={{ minWidth: 180, maxWidth: 320 }}
                    maxTagCount={2}
                    value={clauseFilter}
                    onChange={setClauseFilter}>
                    {champions.map(c => (
                        <Select.Option key={c.clause_id} value={c.clause_id}>
                            {c.clause_code} — {c.clause_title}
                        </Select.Option>
                    ))}
                </Select>
                <Tooltip title="How many facilities to show per clause (1st place only, or a full podium)">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Show top:</span>
                        <Select size="small" style={{ width: 80 }} value={podiumSize} onChange={setPodiumSize}>
                            <Select.Option value={1}>Top 1</Select.Option>
                            <Select.Option value={3}>Top 3</Select.Option>
                            <Select.Option value={5}>Top 5</Select.Option>
                            <Select.Option value={10}>Top 10</Select.Option>
                        </Select>
                    </span>
                </Tooltip>
                <Tooltip title="Minimum assessments a facility needs to qualify for an award — prevents a single lucky audit from winning">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Min. assessments:</span>
                        <InputNumber size="small" min={1} max={20} value={minAssessments}
                            onChange={v => setMinAssessments(v || 1)} style={{ width: 60 }} />
                    </span>
                </Tooltip>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
                    Refresh
                </Button>
                <Button size="small" icon={<FullscreenOutlined />}
                    onClick={() => document.documentElement.requestFullscreen?.()}>
                    Present
                </Button>
            </div>

            <div style={{ background: '#f4f6fb', padding: '32px', minHeight: '70vh' }}>
                <Spin spinning={loading}>
                    {!periodLabel ? (
                        <Empty description="Select a period to view champions" />
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                                <Radio.Group size="small" value={rankMode} onChange={e => setRankMode(e.target.value)}
                                    buttonStyle="solid">
                                    <Radio.Button value="qi">⭐ Quality Index (Audit + Survey)</Radio.Button>
                                    <Radio.Button value="audit">📋 Audit Score Only</Radio.Button>
                                </Radio.Group>
                            </div>
                            
                            <GrandPrizeBanner champion={grandPrize} periodLabel={periodLabel} rankMode={effectiveRankMode} />
                            <OverallLeaderboard
                                list={effectiveRankMode === 'qi' ? overallQI : overallAudit}
                                rankMode={effectiveRankMode}
                                podiumSize={podiumSize}
                            />

                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
                            }}>
                                <StarFilled style={{ color: '#faad14', fontSize: 18 }} />
                                <div style={{ fontWeight: 800, fontSize: 16, color: '#1a1a2e' }}>
                                    Clause Champions — {periodLabel}
                                </div>
                                <Tag color="blue" style={{ marginLeft: 'auto' }}>
                                    {displayedChampions.length} of {champions.length} clause{champions.length !== 1 ? 's' : ''}
                                </Tag>
                            </div>

                            {displayedChampions.length === 0 ? (
                                <Empty description={champions.length === 0
                                    ? "No eligible facilities for this period yet"
                                    : "No clauses match the current filter"} />
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: 18,
                                }}>
                                    {displayedChampions.map(c => (
                                        <PodiumCard key={c.clause_id} champion={c} podiumSize={podiumSize} />
                                    ))}
                                </div>
                            )}

                            <div style={{ textAlign: 'center', marginTop: 40, fontSize: 11, color: '#aaa' }}>
                                Generated {moment().format('DD MMMM YYYY, HH:mm')} · Eligibility: minimum {minAssessments} approved assessment{minAssessments !== 1 ? 's' : ''} per clause this period
                            </div>
                        </>
                    )}
                </Spin>
            </div>
        </div>
    );
}